import React from 'react';
import { useUserRole } from '@/hooks/useUserRole';
import { hasAccessToCategory, getCategoryTier } from '@/config/categoryTiers';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Crown, Stethoscope, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

interface RoleBasedCategoryAccessProps {
  categoryId: string;
  children: React.ReactNode;
  showUpgradePrompt?: boolean;
}

const RoleBasedCategoryAccess: React.FC<RoleBasedCategoryAccessProps> = ({
  categoryId,
  children,
  showUpgradePrompt = true
}) => {
  const { role, loading } = useUserRole();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const hasAccess = hasAccessToCategory(categoryId, role);

  if (hasAccess) {
    return <>{children}</>;
  }

  if (!showUpgradePrompt) {
    return null;
  }

  const tier = getCategoryTier(categoryId);
  const isPractitionerCategory = tier === 'practitioner';
  const isPartnerCategory = tier === 'partner';
  const isAdminCategory = tier === 'admin';

  return (
    <div className="container mx-auto px-6 py-12 max-w-4xl">
      <Card className="border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 shadow-lg">
        <CardContent className="text-center py-12 px-8">
          <div className="mb-6">
            {isPractitionerCategory && (
              <Stethoscope className="h-16 w-16 text-orange-500 mx-auto mb-4" />
            )}
            {isPartnerCategory && (
              <Crown className="h-16 w-16 text-purple-500 mx-auto mb-4" />
            )}
            {isAdminCategory && (
              <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
            )}
            {tier === 'core' && (
              <Crown className="h-16 w-16 text-orange-500 mx-auto mb-4" />
            )}
            <Lock className="h-8 w-8 text-orange-400 mx-auto" />
          </div>

          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            {isPractitionerCategory && 'Practitioner Content'}
            {isPartnerCategory && 'Partner Content'}
            {isAdminCategory && 'Administrator Content'}
            {tier === 'core' && 'Premium Content'}
          </h1>

          <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto">
            {isPractitionerCategory && (
              <>
                This specialized content is designed for licensed practitioners.
                Access requires practitioner-level permissions for advanced meditation and yoga practices.
              </>
            )}
            {isPartnerCategory && (
              <>
                This therapeutic content is designed for partners and specialized providers.
                Access includes relationship, intimacy, and recovery support content.
              </>
            )}
            {isAdminCategory && (
              <>
                This administrative content requires the highest level access.
                Reserved for system administrators and specialized therapeutic applications.
              </>
            )}
            {tier === 'core' && (
              <>
                This premium wellness content requires an upgraded account.
                Unlock advanced features and personalized experiences.
              </>
            )}
          </p>

          <div className="space-y-4">
            {isPractitionerCategory ? (
              <div className="bg-white/60 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-gray-800 mb-2">Practitioner Features Include:</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Specialized therapeutic content</li>
                  <li>• Client management tools</li>
                  <li>• Professional session creation</li>
                  <li>• Advanced personalization options</li>
                </ul>
              </div>
            ) : (
              <div className="bg-white/60 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-gray-800 mb-2">Premium Features Include:</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Unlimited session access</li>
                  <li>• Advanced personalization</li>
                  <li>• Priority content generation</li>
                  <li>• Extended session library</li>
                </ul>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-orange-600 hover:bg-orange-700">
                <Link to="/contact">
                  {isPractitionerCategory ? 'Contact for Practitioner Access' : 'Upgrade Account'}
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/app">Back to Home</Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RoleBasedCategoryAccess;